import { computed, ref, watchEffect } from 'vue'

import { selectGetMoreActions } from '@/features/ui/components/more-actions-menu/store/more-actions.selectors'
import type { MoreActionsStoreType } from '@/features/ui/components/more-actions-menu/store/more-actions.store'
import moreActionsStore from '@/features/ui/components/more-actions-menu/store/more-actions.store'
import useRouter from '@/hooks/use-router.hook'
import useWindowSize from '@/hooks/use-window-size.hook'
import { BREAKPOINTS } from '@/utils/config/constants'
import sleep from '@/utils/misc/sleep'

function useMoreActionsMenu() {
  const { widthRef } = useWindowSize()
  const dataActionIdRef = ref<MoreActionsStoreType['name'] | undefined>(undefined)
  const moreActionsRef = computed(() => selectGetMoreActions(moreActionsStore, dataActionIdRef.value).value)
  const { setAttachedData } = moreActionsStore
  const router = useRouter()

  watchEffect(
    (onCleanup) => {
      let dataActionFadeOutTimeout: any

      // getting the route just to watch for changes
      if (router?.currentRoute.path) {
        //
      }

      const openClass = 'more-actions-menu--open'

      const handleClick = async (e: MouseEvent) => {
        const menu = document.querySelector('.more-actions-menu') as HTMLElement | null
        if (!menu) return

        const target = e.target as HTMLElement
        if (!target) return

        const clickedDataAction = (target.dataset.action ||
          target.closest<HTMLElement>('[data-action]')?.dataset.action) as MoreActionsStoreType['name'] | null

        const dataActionAttachedData =
          target.dataset.attached || target.closest<HTMLElement>('[data-attached]')?.dataset.attached

        const clickedInsideMenu = menu.contains(target)

        if (clickedDataAction) {
          e.preventDefault()
          e.stopPropagation()
          clearTimeout(dataActionFadeOutTimeout)

          if (dataActionAttachedData) {
            try {
              setAttachedData(clickedDataAction, JSON.parse(dataActionAttachedData))
            } catch {
              setAttachedData(clickedDataAction, {})
            }
          }

          menu.classList.add(openClass)
          dataActionIdRef.value = clickedDataAction

          await sleep(10)

          if (widthRef.value >= BREAKPOINTS.DESKTOP) {
            positionMenu(menu, e)
          } else {
            menu.style.left = ''
            menu.style.top = ''
          }
        } else if (!clickedInsideMenu) {
          closeMenu(menu)
        } else if (clickedInsideMenu && dataActionIdRef.value) {
          const isUl = target.tagName.toLowerCase() === 'ul'
          if (isUl) {
            return
          } else {
            closeMenu(menu)
          }
        }
      }

      function positionMenu(menu: HTMLElement, e: MouseEvent) {
        const rect = menu.getBoundingClientRect()
        const x = e.clientX
        const y = e.clientY
        const windowHeight = window.innerHeight
        const safetyMargin = 32

        menu.style.left = x < rect.width - safetyMargin ? `${x}px` : `${x - rect.width}px`
        menu.style.top =
          y < rect.height ? `${y}px` : y > windowHeight - rect.height - safetyMargin ? `${y - rect.height}px` : `${y}px`
      }

      function closeMenu(menu: HTMLElement) {
        menu.classList.remove(openClass)

        if (dataActionIdRef.value) {
          const previousAction = dataActionIdRef.value
          dataActionIdRef.value = undefined
          setAttachedData(previousAction, {})
        }

        if (widthRef.value >= BREAKPOINTS.DESKTOP) {
          menu.style.left = '-100px'
          menu.style.top = '-100px'
        }
      }

      document.addEventListener('click', handleClick)

      onCleanup(() => {
        document.removeEventListener('click', handleClick)
        clearTimeout(dataActionFadeOutTimeout)
      })
    },
    { flush: 'post' },
  ) // update the event listener when the window width changes and reload on pathname changes

  return { moreActionsRef }
}

export default useMoreActionsMenu
